import { getAccessToken, getProfile, getRefreshToken, setAccessToken, setProfile, setRefreshToken } from '@/utils/tokenStore';
import { oneBtnDialog, oneBtnHtmlDialog, jwtDecoder, logging, getMobileLandingPath, isMobileWidth } from "@/utils/utils";
import { isLegacyHostApp } from "@/utils/deviceCheck";
import { localStorageEnums } from "@/enums/localStorage.enums";
import { GEO_BLOCKED_MESSAGE, authErrorMessage, loginStepMode, setMfaToken, withRemainingAttempts } from "@/utils/mfa";
import { reportGeoBlock } from "@/utils/geoBlockReport";

export const signInMixin = {
  methods: {
    // 로그인 전 국외 판정 한 번(enforce·국외면 blocked. 토큰 없이 불러 계정 예외는 모름 — 안내에 예외 계정 한 줄을 덧붙임) → this.geoNotice. 실패는 조용히 무시 — 실제 차단은 서버가 함
    async checkGeo() {
      try {
        const { data, config } = await this.$apiInstance.get("/auth/geo-check");
        if (data?.blocked === true) {
          this.geoNotice = `${GEO_BLOCKED_MESSAGE}${data.ip ? ` (접속 IP: ${data.ip})` : ""}`;
          reportGeoBlock("PAGE_OPEN", config);
        }
      } catch (e) {
        // 판정 API 미배포·네트워크 오류는 안내하지 않음
      }
    },

    /**
     * 로그인 API 호출 (roles 배열 순회)
     * @param {string} email - 사용자 이메일
     * @param {string} password - 사용자 비밀번호
     * @param {Array<string>} roles - 시도할 role 배열
     * @returns {Promise<{response: Object|null, lastError: Error|null}>}
     */
    async performLogin(email, password, roles) {
      let loginResponse = null;
      let lastError = null;

      for (const role of roles) {
        try {
          loginResponse = await this.$apiInstance.post(`/auth/login`, {
            username: email,
            password: password,
            role: role
          });
          break; // 성공하면 루프 종료
        } catch (error) {
          lastError = error;
          // 다음 role로 계속 시도
        }
      }

      return { response: loginResponse, lastError };
    },

    /**
     * 로그인 성공 처리
     * @param {Object} response - API 응답 객체
     * @param {string} email - 사용자 이메일
     */
    async handleLoginSuccess(response, email) {
      // 2차 인증이 필요하면 토큰 대신 2단계(TWO_FACTOR_REQUIRED)·수단 등록(TWO_FACTOR_ENROLL_REQUIRED) 응답이 옴
      // → 인증번호 창(this.$refs.otp)을 거쳐 받은 verify 응답(로그인 응답과 같은 모양)으로 바꿔 끼움
      const otpMode = loginStepMode(response.data);
      if (otpMode) {
        const verified = await this.$refs.otp.start({ ...response.data, mode: otpMode });
        if (!verified) return;
        response = { ...response, data: verified };
      }

      let routingPath;

      if (email === "dentium03") {
        // 2026-08-28: v1 목록 화면 제거로 v3 목록으로 옮겼다.
        routingPath = "ContractListV3";
      } else if (email === this.password) {
        if (email.includes("plasmapp")) {
          routingPath = "Dashboard";
        } else if (email === "dentium") {
          routingPath = "Dashboard";
        } else if (email === "point") {
          routingPath = "Dashboard";
        } else if (email === "withwell") {
          routingPath = "Dashboard";
        } else {
          routingPath = "ResetPwd";
          sessionStorage.setItem("username", email);
        }
      } else {
        routingPath = "Dashboard";
      }

      await oneBtnDialog(this.$i18n.t("signin_login_done_title"), this.$i18n.t("signin_login_done_content"), false);

      setAccessToken(response.data.accessToken);
      setRefreshToken(response.data.refreshToken);
      // 2단계·등록을 통과한 로그인(또는 2FA 예외)만 증표가 있음. 없으면 옛 계정 증표도 지움.
      setMfaToken(response.data.mfaToken);
      await this.getUserInfo();

      window.checkIsLoggedIn({
        isLoggedIn: true,
        accessToken: getAccessToken(),
        refreshToken: getRefreshToken()
      });

      /*
       * 로그인 후 목적지.
       *
       * 예전에는 여기에 `window.innerWidth < 768 && !zeiss` 분기가 있어서,
       * 폰으로 들어오면 위에서 계산한 routingPath 를 버리고 무조건 전자계약으로 보냈다.
       * 권한과 무관하게 동작해서, 메뉴가 하나도 없는 계정도 전자계약 화면을 보게 됐다.
       * 그 분기는 걷어냈다.
       *
       * 지금은 폰이면 '첫 탭' 으로 간다. 마이페이지가 항상 탭에 있으므로
       * 탭이 하나도 없는 경우가 없고, 제조사가 앞에 둔 화면이 곧 진입 화면이 된다.
       *
       * 탭 구성은 API 로 받으므로 이동 전에 한 번 기다린다 —
       * App.vue 의 워처는 이동한 뒤에 받기 때문에 그때는 첫 탭을 알 수 없다.
       *
       * 폭을 여기서 보는 이유:
       * App.vue 의 isMobileView 가드는 그 컴포넌트의 워처에만 걸려 있고,
       * 이 액션을 직접 부르는 여기에는 걸리지 않는다. 그리고
       * getVisibleMobileTabs 는 마이페이지를 항상 붙여 빈 배열이 될 수 없어서
       * '탭이 없으면 PC' 로 구분할 수도 없다. 폭을 보지 않으면 PC 에서도
       * 탭 구성을 받아 오고 탭 경로로 이동해 버린다.
       *
       * PC 는 아래 routingPath 한 줄만 탄다 — 운영과 같은 줄이다.
       *
       * 구버전 앱도 그 줄을 탄다. 네이티브 하단 탭을 쓰는 환경이라 웹의 첫 탭
       * 개념이 없고, 로그인 후 도착지도 예전(대시보드) 그대로여야 한다.
       */
      if (isMobileWidth() && !isLegacyHostApp()) {
        /*
         * 탭 목록은 두 응답의 교집합이라 둘 다 기다려야 한다.
         *
         *   표시할 탭 = 제조사가 고른 탭(getMobileTabs) ∩ 그 계정의 권한(getPrivileges)
         *
         * 예전에는 getMobileTabs 만 기다렸다. 그러면 권한이 비어 있는 채로 첫 탭을
         * 계산해서, 교집합을 통과하는 탭이 하나도 없어 마이페이지(와 덴티움이면
         * 브라이트몰)만 남았다. 잠시 뒤 사이드바가 getPrivileges 를 마치면 그제서야
         * 탭이 늘어나, 로그인 직후 탭이 깜빡이며 바뀌는 것처럼 보였다.
         *
         * 권한을 받아 오는 곳이 사이드바 컴포넌트뿐인 것도 문제였다 —
         * 폰에서는 사이드바가 보이지도 않는데 그 컴포넌트를 기다려야 했다.
         * 여기서 직접 부르면 그 순서에 기대지 않는다.
         *
         * userId 는 store 가 아니라 CompanyInfo 에서 읽는다. 바로 위 getUserInfo 가
         * getCompanyInfo 를 await 하지 않아 store 의 userInfo 는 아직 비어 있을 수 있는데,
         * CompanyInfo 는 같은 함수가 동기적으로 저장해 둔다.
         */
        let userId = null;
        try {
          userId = JSON.parse(getProfile(localStorageEnums.CompanyInfo))
            ?.accountInfo?.username;
        } catch (error) {
          userId = null;
        }

        await Promise.all([
          this.$store.dispatch("getMobileTabs"),
          // 권한 조회가 실패해도 로그인은 진행되어야 한다. 그때는 탭이 마이페이지만 남는다.
          userId
            ? this.$store.dispatch("getPrivileges", userId).catch(() => {})
            : Promise.resolve(),
        ]);

        const landingPath = getMobileLandingPath(this.$store);
        if (landingPath) {
          /*
           * push 가 아니라 replace 다.
           *
           * push 면 /signin 이 히스토리에 남는다. 안드로이드 뒤로가기가 웹뷰 히스토리를
           * 되감으므로(MainActivity.onHandleBackPressed), 첫 탭에서 뒤로가기를 누르면
           * /signin 으로 돌아가고, 가드가 토큰을 보고 대시보드로 튕긴다 —
           * 탭에 없는 화면이 뜨는 그 증상이다.
           *
           * replace 면 히스토리에 남지 않아 안드로이드가 '종료 토스트' 단계로 넘어간다.
           * 앱다운 동작이기도 하다.
           *
           * PC 는 아래 push 를 그대로 탄다 — 동작을 바꾸지 않는다.
           */
          this.$router.replace(landingPath);
          return;
        }
      }

      /*
       * 구버전 앱은 전자계약 목록으로 — 001 이전 운영과 같은 줄이다.
       *
       *   if (window.innerWidth < 768 && !email.includes("zeiss"))
       *       this.$router.push({ name: "ContractList" });
       *
       * 자이스 예외까지 그대로 옮겼다. 그 계정은 예전에도 폰에서 전자계약으로
       * 보내지 않고 아래 routingPath 를 탔다.
       *
       * 구버전 앱에는 웹 하단 탭을 그리지 않으므로(App.vue showMobileChrome)
       * 이동 수단이 네이티브 탭 둘뿐이고, 그 첫 탭 이름이 '전자계약'이다.
       */
      if (isMobileWidth() && isLegacyHostApp() && !email.includes("zeiss")) {
        // 2026-08-28: v1 목록 화면 제거로 v3 목록으로 옮겼다. 착지 화면의 성격
        // (전자계약 목록)은 그대로라 네이티브 첫 탭 이름 '전자계약'과 계속 맞는다.
        this.$router.push({ name: "ContractListV3" });
        return;
      }

      this.$router.push({ name: routingPath });
    },

    /**
     * 로그인 실패 처리 (에러 타입별 분기)
     * @param {Error} lastError - 마지막 에러 객체
     */
    async handleLoginError(lastError) {
      if (!lastError.response) {
        // 네트워크 에러 또는 서버 응답 없음
        await oneBtnDialog(this.$i18n.t("signin_login_err_title"), this.$i18n.t("signin_login_err_content"), false);
        return;
      }

      // 잠금(423)·공용 비밀번호 운영자 거절(SESSION_NOT_ALLOWED) 등 (SPEC-SENDCERT-001 design.md §2-3)
      const codeMessage = authErrorMessage(lastError);
      if (codeMessage) {
        await oneBtnDialog(this.$i18n.t("signin_login_err_title"), codeMessage, false);
        return;
      }

      const status = lastError.response.status;

      if (status === 400) {
        await this.handle400Error(lastError);
      } else if (status === 401) {
        await this.handle401Error(lastError);
      } else {
        await oneBtnDialog(this.$i18n.t("signin_login_err_title"), this.$i18n.t("signin_login_err_content"), false);
      }
    },

    /**
     * 400 에러 처리 (잘못된 계정 타입)
     * @param {Error} error - 에러 객체
     */
    async handle400Error(error) {
      logging().exceptProd("env==>", process.env.NODE_ENV);

      if (error.response.data.message === this.$i18n.t("signin_wrong_type_err_content")) {
        let href;
        if (process.env.NODE_ENV === "development" || process.env.NODE_ENV === "local") {
          href = "https://intest-hospital.certi.life/signin";
        } else if (process.env.NODE_ENV === "stage") {
          href = "https://stg-hospital.certi.life/signin";
        } else if (process.env.NODE_ENV === "production") {
          href = "https://hospital.certi.life/signin";
        }
        await oneBtnHtmlDialog("로그인 실패", `<span>병원 계정입니다.<br><span>아래 링크에서 로그인 해주세요.</span><br><br><a href="${href}" class="forwardLink">🔗 병원 페이지에서 로그인하기</a></span>`, false);
      } else {
        await oneBtnDialog(this.$i18n.t("signin_wrong_access_err_title"), this.$i18n.t("signin_wrong_access_err_content"), false);
      }
    },

    /**
     * 401 에러 처리 (인증 실패)
     * 각 컴포넌트에서 오버라이드하여 사용
     * @param {Error} error - 에러 객체
     */
    async handle401Error(error) {
      console.log(error);

      let message = error.response.data.message;
      if (message == "아이디 또는 비밀번호를 확인해주세요.") {
        message = this.$i18n.t("signin_login_err_content");
      }

      await oneBtnDialog(this.$i18n.t("signin_login_err_title"), withRemainingAttempts(message, error), false);
    },

    /**
     * 사용자 정보 조회
     */
    async getUserInfo() {
      const companyId = jwtDecoder(getAccessToken());
      const response = await this.$apiInstance.get(`/companies/${companyId}`);
      if (response.status === 200 || response.status === 201) {
        const { data } = response;
        setProfile(localStorageEnums.CompanyInfo, JSON.stringify(data));
        if (getAccessToken()) {
          this.$store.dispatch("getCompanyInfo", this);
        }
      }
    }
  }
};
